// Hilfsfunktionen der Suche: Vorschläge für die Autovervollständigung, Beschriftungen, Gruppierung, Fehlertexte.
import { isTextBased, type ChannelDto, type ChannelType, type SearchResultDto, type UserDto } from '@lagerfeuer/shared';
import { useEffect, useState } from 'react';
import { ApiError } from '../../../lib/api';
import { SEARCH_HAS_TYPES, type SearchHasType } from '../api';
import { errorMessage } from '../format';
import { formatFilter, isDateKey, type ActiveToken, type FilterKey, type SearchToken } from './query-parser';

export interface Suggestion {
  id: string;
  label: string;
  detail?: string;
  /** Text, der den aktiven Token ersetzt. */
  insert: string;
}

export const HAS_LABELS: Record<SearchHasType, string> = {
  link: 'Link',
  file: 'Datei',
  image: 'Bild',
  video: 'Video',
  embed: 'Einbettung',
  sticker: 'Sticker',
  sound: 'Ton',
  poll: 'Umfrage',
};

const KEY_SUGGESTIONS: { insert: string; label: string; detail: string }[] = [
  { insert: 'from:', label: 'from:', detail: 'Von einer Person' },
  { insert: 'in:', label: 'in:', detail: 'In einem Kanal' },
  { insert: 'has:', label: 'has:', detail: 'Enthält Link, Datei, Bild …' },
  { insert: 'mentions:', label: 'mentions:', detail: 'Erwähnt eine Person' },
  { insert: 'before:', label: 'before:', detail: 'Vor einem Datum' },
  { insert: 'after:', label: 'after:', detail: 'Nach einem Datum' },
  { insert: 'during:', label: 'during:', detail: 'An einem Tag' },
  { insert: 'pinned:true', label: 'pinned:true', detail: 'Nur angeheftete Nachrichten' },
];

export function keySuggestions(prefix: string): Suggestion[] {
  const p = prefix.toLowerCase();
  return KEY_SUGGESTIONS.filter((s) => s.insert.startsWith(p) && s.insert !== p).map((s) => ({ id: `key-${s.insert}`, ...s }));
}

export function hasSuggestions(prefix: string): Suggestion[] {
  const p = prefix.toLowerCase();
  return SEARCH_HAS_TYPES.filter((t) => t.startsWith(p) || HAS_LABELS[t].toLowerCase().startsWith(p)).map((t) => ({
    id: `has-${t}`,
    label: HAS_LABELS[t],
    detail: `has:${t}`,
    insert: `has:${t}`,
  }));
}

export function pinnedSuggestions(prefix: string): Suggestion[] {
  return [
    { id: 'pinned-true', label: 'Nur angeheftete', detail: 'pinned:true', insert: 'pinned:true' },
    { id: 'pinned-false', label: 'Nicht angeheftete', detail: 'pinned:false', insert: 'pinned:false' },
  ].filter((s) => s.insert.slice('pinned:'.length).startsWith(prefix.toLowerCase()));
}

/** Lokales Datum als YYYY-MM-DD. */
export function isoDay(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function dateSuggestions(key: FilterKey, prefix: string, now: Date = new Date()): Suggestion[] {
  const today = isoDay(now);
  const y = new Date(now);
  y.setDate(y.getDate() - 1);
  const yesterday = isoDay(y);
  return [
    { id: `${key}-today`, label: 'Heute', detail: today, insert: `${key}:${today}` },
    { id: `${key}-yesterday`, label: 'Gestern', detail: yesterday, insert: `${key}:${yesterday}` },
  ].filter((s) => s.detail.startsWith(prefix));
}

export function userSuggestions(key: FilterKey, users: UserDto[], prefix: string): Suggestion[] {
  const p = prefix.toLowerCase();
  return users
    .filter((u) => !p || u.username.toLowerCase().startsWith(p) || (u.displayName ?? '').toLowerCase().startsWith(p))
    .slice(0, 8)
    .map((u) => ({ id: `${key}-user-${u.id}`, label: u.displayName || u.username, detail: `@${u.username}`, insert: formatFilter(key, u.username) }));
}

export function channelSuggestions(channels: ChannelDto[], prefix: string): Suggestion[] {
  const p = prefix.toLowerCase();
  return channels
    .filter((c) => c.name && isTextBased(c.type) && c.name.toLowerCase().includes(p))
    .sort((a, b) => a.position - b.position)
    .slice(0, 10)
    .map((c) => ({ id: `in-${c.id}`, label: `#${c.name}`, detail: CHANNEL_TYPE_LABELS[c.type], insert: formatFilter('in', c.name ?? c.id) }));
}

/** Vorschläge ohne Netzwerkdaten; Nutzer/Kanäle ergänzt die Komponente. */
export function staticSuggestions(active: ActiveToken, now?: Date): Suggestion[] {
  if (active.kind === 'key') return keySuggestions(active.prefix);
  if (active.kind !== 'value') return [];
  if (active.key === 'has') return hasSuggestions(active.prefix);
  if (active.key === 'pinned') return pinnedSuggestions(active.prefix);
  if (isDateKey(active.key)) return dateSuggestions(active.key, active.prefix, now);
  return [];
}

export const CHANNEL_TYPE_LABELS: Record<ChannelType, string> = {
  TEXT: 'Textkanal',
  VOICE: 'Sprachkanal',
  CATEGORY: 'Kategorie',
  ANNOUNCEMENT: 'Ankündigungen',
  FORUM: 'Forum',
  DM: 'Direktnachricht',
  GROUP_DM: 'Gruppenchat',
  PUBLIC_THREAD: 'Thread',
  PRIVATE_THREAD: 'Privater Thread',
};

/** Beschriftung eines Chips für einen Token. */
export function chipLabel(t: SearchToken): string {
  if (t.kind === 'phrase') return `„${t.value}"`;
  if (t.kind === 'exclude') return `Ohne: ${t.value}`;
  if (t.kind === 'word') return t.value;
  const v = t.value || '…';
  switch (t.key) {
    case 'from':
      return `Von: ${v}`;
    case 'in':
      return `In: #${v}`;
    case 'has':
      return `Enthält: ${HAS_LABELS[v as SearchHasType] ?? v}`;
    case 'before':
      return `Vor: ${v}`;
    case 'after':
      return `Nach: ${v}`;
    case 'during':
      return `Am: ${v}`;
    case 'mentions':
      return `Erwähnt: ${v}`;
    case 'pinned':
      return v === 'true' ? 'Angeheftet' : v === 'false' ? 'Nicht angeheftet' : `Angeheftet: ${v}`;
    default:
      return t.raw;
  }
}

export type SearchHit = SearchResultDto['results'][number];

export interface ResultGroup {
  channel: SearchHit['channel'];
  serverId: string | null;
  hits: SearchHit[];
}

/** Treffer nach Kanal gruppieren (Reihenfolge: erstes Auftreten, also gemäß Sortierung). */
export function groupByChannel(hits: SearchHit[]): ResultGroup[] {
  const groups = new Map<string, ResultGroup>();
  for (const h of hits) {
    let g = groups.get(h.channel.id);
    if (!g) {
      g = { channel: h.channel, serverId: h.channel.serverId ?? h.message.serverId ?? null, hits: [] };
      groups.set(h.channel.id, g);
    }
    if (!g.hits.some((x) => x.message.id === h.message.id)) g.hits.push(h);
  }
  return [...groups.values()];
}

/** Eindeutige Autoren der Treffer (für „zuletzt gesehen" in from:/mentions:). */
export function recentAuthors(hits: SearchHit[], max = 8): UserDto[] {
  const seen = new Map<string, UserDto>();
  for (const h of hits) {
    const a = h.message.author;
    if (a && !seen.has(a.id)) seen.set(a.id, a);
    if (seen.size >= max) break;
  }
  return [...seen.values()];
}

export function searchErrorMessage(err: unknown): string {
  if (err instanceof ApiError && (err.status === 429 || err.code === 'RATE_LIMITED')) {
    return 'Zu viele Suchanfragen – bitte einen Moment warten und dann erneut suchen.';
  }
  return errorMessage(err);
}

/** Bei 4xx (außer 408) nicht automatisch wiederholen – v. a. nicht bei 429. */
export function shouldRetrySearch(failureCount: number, err: unknown): boolean {
  if (err instanceof ApiError && err.status >= 400 && err.status < 500 && err.status !== 408) return false;
  return failureCount < 2;
}

/** Wert entprellt übernehmen. */
export function useDebouncedValue<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}
